'use client';

import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo, type ReactNode } from 'react';
import { ApiProvider } from '@/contexts/ApiContext';
import { createQueryClient } from '@/lib/queryClient';
import { detectAndApplyBrowserLanguage } from '@/stores/languageStore';
import { useAuthStoreHydrate } from '@/stores/authStore';
import { WebUrlNavigationProvider } from '@/components/WebUrlNavigationProvider';
import { useYandexMetrika } from '@/hooks/useYandexMetrika';
import { useFacebookPixel } from '@/hooks/useFacebookPixel';
import { prefetchLoadingAssets } from '@/lib/prefetchLoadingAssets';
import { initAudioSession } from '@/lib/audioSession';
import { initClickSound, preloadUiSounds } from '@/lib/uiSounds';

/**
 * Root provider tree. Intentionally does NOT mount CentrifugoProvider —
 * each route group owns its own (anonymous in (public)/, authed inside
 * (auth)/AuthedProviders). The legacy /app/[[...slug]] catch-all
 * mounts TmaApp which carries its own CentrifugoProvider for the
 * legacy stack-nav subtree. This keeps the WebSocket socket count
 * to one per active route.
 *
 * Anonymous ApiProvider stays here; (auth) overrides it with an
 * authenticated ApiClient inside AuthedProviders. Two ApiClient
 * objects coexist when (auth) renders — that's just memory, no
 * external connection.
 */
export function Providers({ children }: { children: ReactNode }) {
  const queryClient = useMemo(() => createQueryClient(), []);
  useAuthStoreHydrate();
  useYandexMetrika();
  useFacebookPixel();

  useEffect(() => {
    detectAndApplyBrowserLanguage();
    prefetchLoadingAssets();
    initAudioSession();
    preloadUiSounds();
    initClickSound();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ApiProvider>
        {/* URL-backed adapter for the legacy useNavigation surface.
         *  Web runtime only — the TMA host short-circuits to
         *  TmaAppMount in layout.tsx and uses its own legacy stack-nav
         *  <NavigationProvider> inside runtime/tma/TmaApp. */}
        <WebUrlNavigationProvider>{children}</WebUrlNavigationProvider>
      </ApiProvider>
    </QueryClientProvider>
  );
}
